import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/SEOHead';
import QuoteFormWidget from '../../../components/feature/QuoteFormWidget';

const SafetyChecklistBlogPost = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Fresno Home Electrical Safety Checklist for 2025',
    description: 'Use our complete Fresno home electrical safety checklist to spot hazards before they become emergencies. Free inspection guide from licensed Fresno electricians.',
    datePublished: '2025-02-14',
    dateModified: '2025-02-14',
    author: { '@type': 'Organization', name: 'Electrician Solutions Fresno' },
    publisher: { '@type': 'Organization', name: 'Electrician Solutions Fresno', url: 'https://electriciansolutionsfresno.com' },
    mainEntityOfPage: 'https://electriciansolutionsfresno.com/blog/fresno-home-electrical-safety-checklist',
  };

  const checklistItems = [
    { zone: 'Electrical Panel', items: ['No tripping breakers or blown fuses', 'Panel is not a Federal Pacific or Zinsco brand', 'No signs of burning, scorching or rust', 'Panel labeled correctly for every circuit', 'Panel has adequate capacity for your home\'s load'] },
    { zone: 'Outlets & Switches', items: ['No cracked, loose or discolored outlets', 'GFCI outlets installed in kitchen, bathrooms, garage, outdoors', 'AFCI breakers on bedroom circuits (required by California code)', 'Outlets are grounded (3-prong)', 'No double-tapped extension cords'] },
    { zone: 'Wiring', items: ['No exposed wiring in attic, crawlspace or walls', 'No aluminum wiring (common in 1960s-70s Fresno homes)', 'All junction boxes covered with face plates', 'Wiring not pinched under insulation', 'No DIY wiring splices or improper connections'] },
    { zone: 'Smoke & CO Detectors', items: ['Smoke detectors in every bedroom and hallway', 'CO detector on every level if home has gas appliances', 'All detectors tested in last 6 months', 'No detectors older than 10 years', 'Detectors interconnected (all alarm when one triggers)'] },
    { zone: 'Outdoor Electrical', items: ['Outdoor outlets have weatherproof covers', 'Outdoor outlets are GFCI protected', 'No extension cords used permanently outdoors', 'Exterior light fixtures sealed against moisture', 'Pool/spa equipment bonded and GFCI protected'] },
  ];

  return (
    <>
      <SEOHead
        title="Home Electrical Safety Checklist Fresno CA 2025 | Electrician Solutions Fresno"
        description="Use our complete Fresno home electrical safety checklist to spot hazards before they become emergencies. Free inspection guide from licensed Fresno electricians."
        canonical="/blog/fresno-home-electrical-safety-checklist"
        keywords="home electrical safety Fresno, electrical safety checklist Fresno CA, electrical inspection Fresno, electrical hazards Fresno"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-28 pb-0 bg-[#1a1a1a]">
          <div className="max-w-[900px] mx-auto px-6 pt-6 pb-10">
            <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6">
              <Link to="/" className="hover:text-[#FFD700] transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/blog" className="hover:text-[#FFD700] transition-colors">Blog</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-gray-400">Safety</span>
            </nav>
            <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4">Safety</span>
            <h1 className="text-3xl sm:text-5xl font-black text-white mb-5 leading-tight">
              Fresno Home Electrical Safety Checklist for 2025
            </h1>
            <div className="flex items-center gap-4 text-gray-500 text-sm">
              <span>February 14, 2025</span><span>·</span><span>8 min read</span><span>·</span>
              <span className="text-[#FFD700]">Electrician Solutions Fresno</span>
            </div>
          </div>
        </section>

        <section className="max-w-[900px] mx-auto px-6 -mt-2">
          <div className="rounded-2xl overflow-hidden h-72 sm:h-[440px]">
            <img
              src="https://public.readdy.ai/ai/img_res/1f72c93b4ac79a7b524da2602a8197f2.jpg"
              alt="Fresno home electrical safety inspection checklist"
              className="w-full h-full object-cover"
            />
          </div>
        </section>

        <article className="max-w-[900px] mx-auto px-6 py-12">
          <p className="text-gray-600 text-lg leading-relaxed mb-8">
            Electrical fires are the third leading cause of home fires in California. Older Fresno neighborhoods — including Tower District, Fig Garden, and Sunnyside — have a higher percentage of aging wiring systems that may not meet current California electrical code. Use this checklist to identify potential hazards before they become emergencies.
          </p>

          <div className="bg-[#fffbea] border-l-4 border-[#FFD700] p-5 rounded-r-xl mb-8">
            <p className="text-sm font-bold text-[#1a1a1a]">Important: This checklist is a guide, not a substitute for a professional inspection. For complete peace of mind, book a <Link to="/services/residential-electrical/home-electrical-inspection" className="text-[#FFD700] underline">professional home electrical inspection</Link> with Electrician Solutions Fresno.</p>
          </div>

          {checklistItems.map((zone, zi) => (
            <div key={zi} className="mb-8">
              <h2 className="text-xl font-black text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-[#FFD700] rounded-full text-[#1a1a1a] font-black text-sm">{zi + 1}</span>
                {zone.zone}
              </h2>
              <div className="space-y-2">
                {zone.items.map((item, ii) => (
                  <div key={ii} className="flex items-start gap-3 p-3 bg-[#f5f5f5] rounded-lg">
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5 border-2 border-gray-300 rounded"></span>
                    <span className="text-gray-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">When to Call a Fresno Electrician Immediately</h2>
          <div className="space-y-3 mb-8">
            {[
              { issue: 'Burning smell from outlets or panel', action: 'This is an emergency — call immediately and do not use the circuit' },
              { issue: 'Sparks from outlets or switches', action: 'Turn off the circuit at the panel and call a Fresno emergency electrician' },
              { issue: 'Lights flickering throughout the home', action: 'May indicate a loose main connection — a serious fire hazard' },
              { issue: 'Warm or hot outlet or switch plates', action: 'Indicates overloaded circuit or faulty wiring — stop using and call us' },
            ].map((item, i) => (
              <div key={i} className="grid grid-cols-[1fr_1.5fr] gap-4 p-4 bg-red-50 border border-red-200 rounded-xl text-sm">
                <div className="font-bold text-red-700">{item.issue}</div>
                <div className="text-gray-600">{item.action}</div>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">Book a Professional Electrical Safety Inspection in Fresno</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Our licensed Fresno electricians perform comprehensive <Link to="/services/residential-electrical/home-electrical-inspection" className="text-[#FFD700] font-bold hover:underline">home electrical inspections</Link>, <Link to="/services/testing-certification/thermal-imaging-electrical-inspections" className="text-[#FFD700] font-bold hover:underline">thermal imaging surveys</Link>, and <Link to="/services/testing-certification/residential-electrical-safety-inspections" className="text-[#FFD700] font-bold hover:underline">residential safety certifications</Link> throughout Fresno. We issue a written report with all findings and recommendations.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {[
              { label: 'Home Electrical Inspection', path: '/services/residential-electrical/home-electrical-inspection', desc: 'Full Fresno home electrical safety audit' },
              { label: 'Thermal Imaging Inspections', path: '/services/testing-certification/thermal-imaging-electrical-inspections', desc: 'Find hidden hot spots before they cause fires' },
              { label: 'Residential Safety Inspections', path: '/services/testing-certification/residential-electrical-safety-inspections', desc: 'Certified electrical safety report for your home' },
              { label: 'Smoke Detector Installation', path: '/services/residential-electrical/smoke-detector-installation', desc: 'Hardwired interconnected smoke detectors Fresno' },
            ].map(s => (
              <Link key={s.path} to={s.path} className="flex items-start gap-4 p-4 border-2 border-gray-100 hover:border-[#FFD700] rounded-xl transition-all cursor-pointer group">
                <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <i className="ri-arrow-right-circle-fill text-[#FFD700] text-xl"></i>
                </span>
                <div>
                  <p className="font-black text-[#1a1a1a] text-sm">{s.label}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{s.desc}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 bg-[#1a1a1a] rounded-2xl p-8 sm:p-10">
            <h3 className="text-2xl font-black text-white mb-3">Book Your Fresno Home Electrical Inspection</h3>
            <p className="text-gray-400 text-sm mb-6">Don't wait for an electrical emergency. Our licensed inspectors will give you a complete written report and fix any issues found.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#FFD700] text-[#1a1a1a] px-8 py-4 rounded-full font-black text-sm uppercase tracking-wide hover:bg-[#FFC700] transition-all cursor-pointer whitespace-nowrap">
              Book Inspection <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="mt-14">
            <h3 className="text-lg font-black text-[#1a1a1a] mb-6">More Fresno Electrical Guides</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { title: 'Why Fresno Landlords Need Annual Electrical Safety Checks', slug: 'landlord-electrical-safety-checks-fresno', cat: 'Commercial' },
                { title: 'How to Know When to Upgrade Your Electrical Panel in Fresno', slug: 'when-to-upgrade-electrical-panel-fresno', cat: 'Residential' },
              ].map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="p-5 border-2 border-gray-100 hover:border-[#FFD700] rounded-xl transition-all cursor-pointer group">
                  <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-3">{p.cat}</span>
                  <p className="font-black text-[#1a1a1a] text-sm leading-snug">{p.title}</p>
                  <span className="text-[#FFD700] text-xs font-bold mt-2 flex items-center gap-1">Read <i className="ri-arrow-right-line"></i></span>
                </Link>
              ))}
            </div>
          </div>
        </article>

        <div className="max-w-[900px] mx-auto px-6 pb-16"><QuoteFormWidget /></div>
        <Footer />
      </div>
    </>
  );
};

export default SafetyChecklistBlogPost;
